/**
 * Convert ForensicOverlay → Cytoscape elements (nodes + edges).
 *
 * Used by Workbench load effect to auto-add forensic-only addresses to canvas.
 * Nodes already on canvas are decorated via a separate pass (see
 * decorateExistingElementsWithOverlay) rather than duplicated.
 */
import type { ForensicOverlay, ForensicOverlayEdge, ForensicOverlayNode } from '../services/api';

export interface ForensicNodeData {
    id: string;
    address: string;
    chain: string;
    label: string;
    entity_name?: string;
    /** Aligns with the synthesized placeholder convention introduced for
     *  coinbase / contract-creation / OP_RETURN counterparties: ghost nodes
     *  set type='placeholder' so the expandNode guard at the top of the
     *  Workbench expand path blocks accidental click-to-expand on them. */
    type?: 'address' | 'placeholder';
    forensic: 'self' | 'counterparty' | 'ghost';
    /** True when this node is a placeholder for an undisclosed counterparty.
     *  Drives dashed gray border + "?" glyph + "Undisclosed in subpoena" tooltip. */
    forensic_ghost?: boolean;
    is_placeholder?: boolean;
    forensic_kyc: {
        names: string[];
        emails: string[];
        exchanges: string[];
        exchange_user_ids: string[];
    };
    forensic_labels: string[];
    forensic_record_ids: string[];
    forensic_movement_count: number;
}

export interface ForensicEdgeData {
    id: string;
    source: string;
    target: string;
    /** 'proof' = verified hash + counterparty present (smoking gun anchor)
     *  'verified' = on-chain hash but no counterparty
     *  'internal_only' = exchange-internal ID, no on-chain anchor
     *  'undisclosed' = counterparty side is a ghost (exchange withheld it)
     */
    forensic_edge: 'proof' | 'verified' | 'internal_only' | 'undisclosed';
    direction: 'deposit' | 'withdrawal';
    forensic_record_id: string;
    tx_hash: string | null;
    exchange_internal_id: string | null;
    amount: string | null;
    currency: string | null;
    timestamp: string | null;
    label?: string;
    /** Same field name as the on-chain BTC fetcher: this edge is one of the
     *  non-primary edges of a multi-input/output UTXO tx. Selector
     *  `edge[?utxo_secondary]` styles both forensic and on-chain co-spends
     *  uniformly in GraphCanvas. */
    utxo_secondary?: boolean;
}


function nodeToCytoscape(n: ForensicOverlayNode) {
    const isGhost = n.kind === 'forensic_ghost';
    const firstName = n.kyc.names[0];
    const firstExchange = n.kyc.exchanges[0];
    const firstLabel = n.labels[0];
    // Ghost label: "? Undisclosed (Binance)" — preserves which exchange withheld the address.
    // Real-node label: KYC name > freeform label > exchange.
    const displayLabel = isGhost
        ? (firstExchange ? `? Undisclosed (${firstExchange})` : '? Undisclosed in subpoena')
        : (firstName || firstLabel || (firstExchange ? `${firstExchange} customer` : n.address.slice(0, 8)));
    const forensic: ForensicNodeData['forensic'] =
        n.kind === 'forensic_self' ? 'self' : n.kind === 'forensic_ghost' ? 'ghost' : 'counterparty';
    return {
        data: {
            id: n.address,
            address: n.address,
            chain: n.chain,
            label: displayLabel,
            entity_name: isGhost ? null : (firstName || firstLabel || null),
            type: isGhost ? 'placeholder' : 'address',
            forensic,
            forensic_ghost: isGhost,
            is_placeholder: isGhost || undefined,
            forensic_kyc: n.kyc,
            forensic_labels: n.labels,
            forensic_record_ids: n.record_ids,
            forensic_movement_count: n.movement_count,
        } as ForensicNodeData,
    };
}


/** Strength ranking for tiers — higher wins when consolidating multiple rows on the same pair. */
const TIER_RANK: Record<ForensicEdgeData['forensic_edge'], number> = {
    proof: 4,
    verified: 3,
    internal_only: 2,
    undisclosed: 1,
};

function tierOf(e: ForensicOverlayEdge): ForensicEdgeData['forensic_edge'] {
    if (e.is_undisclosed) return 'undisclosed';
    return e.is_proof_anchor ? 'proof' : (e.verified ? 'verified' : 'internal_only');
}

function fmtTokenSum(val: number, name: string): string {
    if (!isFinite(val)) return `${val} ${name}`;
    const maxDec = (name === 'BTC' || name === 'ETH' || name === 'TRX') && val < 0.01 ? 8 : (val < 1 ? 4 : 2);
    return `${val.toLocaleString(undefined, { maximumFractionDigits: maxDec })} ${name}`;
}

/**
 * Consolidate multiple forensic rows between the same (source, target) into one edge.
 * Mirrors how transaction edges are aggregated elsewhere — N subpoena rows = 1 edge with summed amounts.
 */
function consolidateForensicEdges(
    rawEdges: ForensicOverlayEdge[]
): { data: ForensicEdgeData }[] {
    const groups = new Map<string, ForensicOverlayEdge[]>();
    for (const e of rawEdges) {
        if (!e.source_address || !e.target_address) continue;
        const key = `${e.direction}:${e.source_address}->${e.target_address}`;
        const arr = groups.get(key);
        if (arr) arr.push(e);
        else groups.set(key, [e]);
    }

    const out: { data: ForensicEdgeData }[] = [];
    for (const [, edges] of groups) {
        // Strongest tier present in the group
        let bestTier: ForensicEdgeData['forensic_edge'] = 'undisclosed';
        for (const e of edges) {
            const t = tierOf(e);
            if (TIER_RANK[t] > TIER_RANK[bestTier]) bestTier = t;
        }

        // Sum amounts per currency
        const tokenSums = new Map<string, number>();
        for (const e of edges) {
            if (!e.amount || !e.currency) continue;
            const v = parseFloat(e.amount);
            if (!isFinite(v)) continue;
            tokenSums.set(e.currency, (tokenSums.get(e.currency) || 0) + v);
        }

        const first = edges[0];
        const txCount = edges.length;
        // Aggregated edge keeps utxo_secondary=true if ANY of its rows is a
        // secondary — the dotted-line style applies; the primary edge of a
        // multi-input row stays as a normal solid line.
        const utxoSecondary = edges.some(e => e.utxo_secondary);
        // "Co-spent" copy hint for the label when this address participated in a
        // multi-input/output tx (true for both primary and secondary edges of
        // such a row, detected via all_input_addresses).
        const isPartOfUtxoGroup = edges.some(e =>
            (Array.isArray(e.all_input_addresses) && e.all_input_addresses.length > 1) ||
            (Array.isArray(e.all_output_addresses) && e.all_output_addresses.length > 1)
        );
        const amountLabel = tokenSums.size > 0
            ? Array.from(tokenSums.entries())
                .sort((a, b) => b[1] - a[1])
                .map(([name, val]) => fmtTokenSum(val, name))
                .join(' + ')
            : 'subpoena';
        const countLabel = txCount > 1 ? `${txCount} txs · ` : '';
        const tierTag =
            bestTier === 'proof' ? ' ⚡ PROOF' :
            bestTier === 'internal_only' ? ' (int-id)' :
            bestTier === 'undisclosed' ? ' · undisclosed cp' : '';
        const coSpentTag = isPartOfUtxoGroup ? ' · co-spent' : '';

        // Carry the proof anchor's hash if any — that's the strongest single piece of evidence
        const proofRow = edges.find(e => e.is_proof_anchor) || edges.find(e => e.verified) || first;
        const id = `forensic:agg:${first.direction}:${first.source_address}:${first.target_address}`;

        out.push({
            data: {
                id,
                source: first.source_address!,
                target: first.target_address!,
                forensic_edge: bestTier,
                direction: first.direction,
                forensic_record_id: first.record_id,
                tx_hash: proofRow.tx_hash,
                exchange_internal_id: proofRow.exchange_internal_id,
                amount: tokenSums.size > 0 ? Array.from(tokenSums.values())[0].toString() : null,
                currency: tokenSums.size > 0 ? Array.from(tokenSums.keys())[0] : null,
                timestamp: first.timestamp,
                label: `${countLabel}${amountLabel}${tierTag}${coSpentTag}`,
                utxo_secondary: utxoSecondary || undefined,
            },
        });
    }
    return out;
}


/**
 * Build element list for addNodesToCanvas. Skips nodes whose id is already on canvas
 * (those will be decorated via a separate pass).
 */
export function buildForensicElements(
    overlay: ForensicOverlay,
    existingNodeIds: Set<string>
): any[] {
    const elements: any[] = [];
    for (const n of overlay.nodes) {
        if (!existingNodeIds.has(n.address)) {
            elements.push(nodeToCytoscape(n));
        }
    }
    for (const cy of consolidateForensicEdges(overlay.edges)) {
        elements.push(cy);
    }
    return elements;
}


/**
 * Decorate existing canvas nodes in-place with overlay data. Returns a new array.
 * Does NOT touch non-overlay nodes.
 */
export function decorateExistingElementsWithOverlay(elements: any[], overlay: ForensicOverlay): any[] {
    const nodeByAddr = new Map<string, ForensicOverlayNode>();
    for (const n of overlay.nodes) nodeByAddr.set(n.address, n);
    return elements.map(el => {
        if (el.data?.source) return el; // edge — leave alone
        const overlayNode = nodeByAddr.get(el.data?.id);
        if (!overlayNode) return el;
        return {
            ...el,
            data: {
                ...el.data,
                forensic: overlayNode.kind === 'forensic_self' ? 'self' : 'counterparty',
                forensic_kyc: overlayNode.kyc,
                forensic_labels: overlayNode.labels,
                forensic_record_ids: overlayNode.record_ids,
                forensic_movement_count: overlayNode.movement_count,
            },
        };
    });
}
